import React, { useEffect, useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { useGetAllCategoriesQuery } from '../../../app/services/productApi';

const FilterBar = ({
  searchQuery,
  setSearchQuery,
  categoryFilter,
  setCategoryFilter,
  brandFilter,
  setBrandFilter,
  brands = [],
  brandsLoading,
  user, // Receive the user object
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery);
  // eslint-disable-next-line
  const [suggestions, setSuggestions] = useState([]);

  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategoriesQuery();

  const uniqueBrands = useMemo(() => {
    return [...new Map(brands.filter(Boolean).map((b) => [b?._id, b])).values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [brands]);

  const uniqueCategories = useMemo(() => {
    return [...new Map((categoriesData?.data || []).map((c) => [c?._id, c])).values()].sort(
      (a, b) => a.name.localeCompare(b.name),
    );
  }, [categoriesData]);

  const isBrandAndSeller = user?.role?.includes('brand') && user?.role?.includes('seller');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch, setSearchQuery]);

  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
  };

  const clearSearch = () => {
    setLocalSearch('');
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 gap-x-4 mb-6">
      {/* Search Input */}
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-3 top-2.5 h-4 w-4 tertiary-text-color" />
        <input
          type="text"
          placeholder="Search"
          value={localSearch}
          onChange={handleSearchChange}
          className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
        />
        {localSearch && (
          <X
            className="absolute right-3 top-2.5 h-4 w-4 tertiary-text-color cursor-pointer hover:text-secondary"
            onClick={clearSearch}
          />
        )}
        {suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded-md bg-white border border-gray-200 shadow-sm max-h-48 overflow-auto">
            {suggestions.map((s, idx) => (
              <li
                key={idx}
                onClick={() => {
                  setLocalSearch(s);
                  setSearchQuery(s);
                }}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              >
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        {categoriesLoading ? (
          <div className="w-32">...Loading</div>
        ) : (
          <select
            className="border border-gray-300 rounded-lg text-sm border-color focus:border-[#832474] focus:outline-none focus:ring-2 p-2 w-full sm:w-auto"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.name}
              </option>
            ))}
          </select>
        )}

        {/* Brand Filter */}
        {!isBrandAndSeller &&
          (brandsLoading ? (
            <div className="w-32">...Loading</div>
          ) : (
            <select
              className="border border-gray-300 rounded-lg text-sm border-color focus:border-[#832474] focus:outline-none focus:ring-2 p-2 w-full sm:w-auto"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <option value="">All Brands</option>
              {uniqueBrands.map((brand) => (
                <option key={brand._id} value={brand._id}>
                  {brand.name}
                </option>
              ))}
            </select>
          ))}
      </div>
    </div>
  );
};

export default FilterBar;
